import { getFirebaseToken } from '@/utils/auth';

const API_BASE_URL = '/.netlify/functions';

interface ProfileData {
  // Core biodata fields (stored in biodata_details table)
  fullName: string;
  dateOfBirth: string;
  gender: 'male' | 'female' | 'other';
  heightCm?: number;
  weightKg?: number;
  displayEmail?: string;
  residenceCity?: string;
  residenceCountry?: string;
  religion?: string;
  casteOrSect?: string;
  motherTongue?: string;
  aboutMe?: string;
  // Family fields (stored in family_details table)
  fatherName?: string;
  fatherOccupation?: string;
  motherName?: string;
  motherOccupation?: string;
  numberOfSiblings?: number;
  familyLocation?: string;
  // Privacy settings
  privateFields?: {
    biodata?: Record<string, boolean>;
    family?: Record<string, boolean>;
  };
}

/**
 * Enhanced biodataService implementation using PostgreSQL via Netlify functions
 */
export const biodataService = {
  /**
   * Get complete profile data including biodata, family, and contact details
   * @returns Complete profile data or null if not found
   */
  async getProfile(): Promise<any> {
    try {
      const token = await getFirebaseToken();
      const response = await fetch(`${API_BASE_URL}/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.profile;
    } catch (error) {
      console.error('Failed to get profile:', error);
      throw error;
    }
  },

  /**
   * Create or update complete profile data with privacy settings
   * @param profileData Complete profile data including privacy settings
   * @returns Updated profile information
   */
  async saveProfile(profileData: ProfileData): Promise<any> {
    try {
      const token = await getFirebaseToken();
      const response = await fetch(`${API_BASE_URL}/profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to save profile:', error);
      throw error;
    }
  },

  // Legacy method compatibility
  async getBiodataByProfileId(profileId: string) {
    const profile = await this.getProfile();
    return profile ? {
      id: profile.id,
      profile_id: profileId,
      height_cm: profile.height_cm,
      weight_kg: profile.weight_kg,
      blood_group: profile.blood_group,
      nationality: profile.nationality,
      sect: profile.sect,
      mother_tongue: profile.mother_tongue,
      hobbies: profile.hobbies,
      about_me: profile.about_me,
      private_fields: profile.biodata_private_fields,
    } : null;
  },

  async createBiodata(profileId: string, data: any) {
    const profileData: ProfileData = {
      fullName: data.full_name || '',
      gender: data.gender || 'other',
      dateOfBirth: data.date_of_birth || '',
      religion: data.religion || '',
      heightCm: data.height_cm,
      weightKg: data.weight_kg,
      displayEmail: data.display_email,
      residenceCity: data.residence_city,
      residenceCountry: data.residence_country,
      casteOrSect: data.caste_or_sect,
      motherTongue: data.mother_tongue,
      aboutMe: data.about_me,
      privateFields: {
        biodata: data.private_fields || {}
      }
    };
    
    return await this.saveProfile(profileData);
  },

  async updateBiodata(biodataId: string, data: any) {
    return await this.createBiodata(biodataId, data);
  },

  async getOrCreateBiodata(profileId: string, defaultData: any = {}) {
    const existingProfile = await this.getProfile();
    if (existingProfile) {
      return await this.getBiodataByProfileId(profileId);
    }
    
    return await this.createBiodata(profileId, defaultData);
  },

  async deleteBiodata(biodataId: string) {
    // For now, we'll implement this as clearing the biodata fields
    const profileData: Partial<ProfileData> = {
      heightCm: undefined,
      weightKg: undefined,
      displayEmail: undefined,
      residenceCity: undefined,
      residenceCountry: undefined,
      casteOrSect: undefined,
      motherTongue: undefined,
      aboutMe: undefined,
      privateFields: {
        biodata: {}
      }
    };
    
    await this.saveProfile(profileData as ProfileData);
    return true;
  }
};
